// UI Builder Modal with Drag and Drop
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  X,
  Plus,
  Trash2,
  Save,
  Loader2,
  LayoutTemplate,
  Search,
  Columns3,
  ArrowLeftRight,
  CheckCircle2,
  Wrench,
  Hash,
  CalendarDays,
  ToggleLeft,
  Link2,
  List as ListIcon,
  Type as TypeIcon,
  Inbox,
  GripVertical,
} from "lucide-react";

const fieldTypeIcon = (f) => {
  if (f?.reference_table) return Link2;
  const t = (f?.field_type || "").toUpperCase();
  if (t === "ENUM" || t === "CHOICE") return ListIcon;
  if (t.includes("BOOL")) return ToggleLeft;
  if (t.includes("TIMESTAMP") || t.includes("DATE")) return CalendarDays;
  if (t.includes("INT") || t.includes("NUMERIC") || t.includes("DECIMAL")) return Hash;
  return TypeIcon;
};

const UiBuilderModal = ({
  tableId,
  tableName,
  isOpen,
  onClose,
  onSaved,
  defaultViewType = "form",
}) => {
  const token = localStorage.getItem("token");
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [allFields, setAllFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [viewType, setViewType] = useState(defaultViewType);
  const [viewName, setViewName] = useState("");
  const [viewId, setViewId] = useState(null);
  const [availableViews, setAvailableViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [search, setSearch] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!tableId || !isOpen) return;

    axios
      .get(`${apiBaseUrl}/search/table_fields/?table_id=${tableId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setAllFields(res.data.records || []));

    axios
      .get(`${apiBaseUrl}/search/views/?sip_table=${tableId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setAvailableViews(res.data.records || []));
  }, [tableId, isOpen]);

  const loadView = async (view) => {
    setSelectedView(view);
    setViewName(view.sip_name);
    setViewType(view.sip_view_type || "form");
    setViewId(view.id);
    setIsDefault(!!view.sip_is_default);

    const res = await axios.get(
      `${apiBaseUrl}/search/view_columns/?sip_view_id=${view.id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const cols = (res.data.records || []).sort(
      (a, b) => (a.sip_column_order || 0) - (b.sip_column_order || 0)
    );
    setSelectedFields(cols);
  };

  const resetToNew = () => {
    setSelectedView(null);
    setViewName("");
    setViewId(null);
    setViewType(defaultViewType);
    setIsDefault(true);
    setSelectedFields([]);
  };

  const removeField = (fieldName) => {
    setSelectedFields((s) => s.filter((f) => f.sip_field_name !== fieldName));
  };

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const draggedFieldName = draggableId
      .replace(/^available-/, "")
      .replace(/^selected-/, "");

    // Reorder within Selected
    if (source.droppableId === "selected" && destination.droppableId === "selected") {
      const reordered = [...selectedFields];
      const movingIndex = reordered.findIndex((f) => f.sip_field_name === draggedFieldName);
      if (movingIndex === -1) return;
      const [moved] = reordered.splice(movingIndex, 1);
      reordered.splice(destination.index, 0, moved);
      setSelectedFields(reordered);
      return;
    }

    // Available → Selected
    if (source.droppableId === "available" && destination.droppableId === "selected") {
      const field = allFields.find((f) => f.name === draggedFieldName);
      if (!field) return;
      const newField = {
        sip_field_name: field.name,
        sip_position: viewType === "list" ? "center" : "left",
        sip_column_order: destination.index + 1,
      };
      const updated = [...selectedFields];
      updated.splice(destination.index, 0, newField);
      setSelectedFields(updated);
      return;
    }

    // Selected → Available (remove)
    if (source.droppableId === "selected" && destination.droppableId === "available") {
      setSelectedFields((s) => s.filter((f) => f.sip_field_name !== draggedFieldName));
      return;
    }
  };

  const addFieldAt = (field, index) => {
    const newField = {
      sip_field_name: field.name,
      sip_position: viewType === "list" ? "center" : "left",
      sip_column_order: index + 1,
    };
    const updated = [...selectedFields];
    updated.splice(index, 0, newField);
    setSelectedFields(updated);
  };

  const determineAlignment = () => {
    let splitState = "center"; // default to center
    return selectedFields.map((f) => {
      if (f.sip_field_name === "start-split") splitState = "left";
      else if (f.sip_field_name === "split") splitState = "right";
      else if (f.sip_field_name === "end-split") splitState = "center";

      return {
        ...f,
        sip_position: f.sip_position === "splitter" ? "splitter" : splitState,
      };
    });
  };

  const addSplitter = (type) =>
    setSelectedFields((prev) => [
      ...prev,
      {
        sip_field_name: type,
        sip_position: "splitter",
        sip_column_order: prev.length + 1,
      },
    ]);

  const handleSave = async () => {
    if (!viewName?.trim()) {
      alert("Please provide a view name");
      return;
    }
    if (!viewType) {
      alert("Please choose a view type");
      return;
    }

    try {
      setSaving(true);
      let view;

      if (!viewId) {
        const res = await axios.post(
          `${apiBaseUrl}/views/`,
          {
            sip_name: viewName.trim(),
            sip_view_type: viewType,
            sip_table: tableId,
            sip_is_default: isDefault,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        view = res.data?.record?.id;
      } else {
        await axios.put(
          `${apiBaseUrl}/views/${viewId}`,
          {
            sip_name: viewName.trim(),
            sip_view_type: viewType,
            sip_table: tableId,
            sip_is_default: isDefault,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        view = viewId;

        // wipe existing columns
        await axios.delete(`${apiBaseUrl}/delete/view_columns/?sip_view_id=${viewId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      const alignedFields = determineAlignment();
      const columns = alignedFields.map((f, i) => ({
        sip_view_id: view,
        sip_field_name: f.sip_field_name,
        sip_column_order: i + 1,
        sip_position: f.sip_position,
        sip_custom_label: f.sip_custom_label || null,
      }));

      for (let col of columns) {
        await axios.post(`${apiBaseUrl}/view_columns/`, col, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Failed to save view.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!viewId) return;
    const ok = window.confirm("Delete this view? This cannot be undone.");
    if (!ok) return;

    try {
      await axios.delete(`${apiBaseUrl}/views/${viewId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Failed to delete view.");
    }
  };

  const availableFields = useMemo(() => {
    const taken = new Set(selectedFields.map((sf) => sf.sip_field_name));
    const filtered = (allFields || []).filter((f) => !taken.has(f.name));
    if (!search.trim()) return filtered;
    const q = search.toLowerCase();
    return filtered.filter(
      (f) =>
        f.label?.toLowerCase().includes(q) ||
        f.name?.toLowerCase().includes(q) ||
        f.field_type?.toLowerCase().includes(q)
    );
  }, [allFields, selectedFields, search]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={styles.headerIcon}>
              <LayoutTemplate size={16} />
            </span>
            <span style={styles.crumb}>UI Builder</span>
            <span style={styles.sep}>/</span>
            <span style={styles.crumbAccent}>{tableName}</span>
          </div>
          <button onClick={onClose} style={styles.iconBtn} title="Close">
            <X size={16} />
          </button>
        </div>

        {/* View picker row */}
        <div style={styles.row}>
          <div style={{ display: "grid", gap: 8, flex: 1 }}>
            <label style={styles.label}>View</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select
                value={selectedView?.id || ""}
                onChange={(e) => {
                  const v = availableViews.find((x) => x.id === e.target.value);
                  v ? loadView(v) : resetToNew();
                }}
                style={styles.select}
                title="Existing views"
              >
                <option value="">＋ New View</option>
                {availableViews.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.sip_name} ({v.sip_view_type})
                  </option>
                ))}
              </select>

              {viewId && (
                <button
                  onClick={handleDelete}
                  style={{ ...styles.btnDangerOutline, display: "inline-flex", alignItems: "center", gap: 6 }}
                  title="Delete view"
                >
                  <Trash2 size={13} /> Delete
                </button>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 8, flex: 1 }}>
            <label style={styles.label}>View name</label>
            <input
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="e.g. Compact Form"
              style={styles.input}
            />
          </div>

          <div style={{ display: "grid", gap: 8, width: 240 }}>
            <label style={styles.label}>Type</label>
            <div style={styles.segmented}>
              <button
                type="button"
                onClick={() => setViewType("form")}
                style={{ ...styles.segment, ...(viewType === "form" ? styles.segmentActive : {}) }}
              >
                Form
              </button>
              <button
                type="button"
                onClick={() => setViewType("list")}
                style={{ ...styles.segment, ...(viewType === "list" ? styles.segmentActive : {}) }}
              >
                List
              </button>
            </div>
            <label style={styles.switchRow}>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              <span>Set as default</span>
            </label>
          </div>
        </div>

        {/* Buckets */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <div style={styles.grid}>
            {/* Available */}
            <Droppable droppableId="available">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} style={styles.bucket}>
                  <div style={styles.bucketHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <strong>Available Fields</strong>
                      <span style={styles.countPill}>{availableFields.length}</span>
                    </div>
                    <div style={{ position: "relative" }}>
                      <Search size={13} style={styles.searchIcon} />
                      <input
                        placeholder="Search name, label, type…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={styles.searchInput}
                      />
                    </div>
                  </div>

                  {availableFields.length === 0 && (
                    <div style={styles.empty}>
                      <Inbox size={20} color="var(--muted, #5c6d6f)" style={{ marginBottom: 6 }} />
                      <div>Nothing to add. Try clearing search or remove from “Selected”.</div>
                    </div>
                  )}

                  {availableFields.map((f, i) => {
                    const FieldIcon = fieldTypeIcon(f);
                    return (
                      <Draggable draggableId={`available-${f.name}`} index={i} key={f.name}>
                        {(p) => (
                          <div
                            ref={p.innerRef}
                            {...p.draggableProps}
                            {...p.dragHandleProps}
                            style={{ ...styles.fieldItem, ...p.draggableProps.style }}
                            title={f.field_type}
                          >
                            <GripVertical size={14} color="var(--line, #dfe7e5)" style={{ flexShrink: 0 }} />
                            <span style={styles.fieldTypeIconWrap}>
                              <FieldIcon size={13} />
                            </span>
                            <div style={{ display: "grid", flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600 }}>{f.label || f.name}</div>
                              <div style={styles.metaSmall}>{f.field_type}</div>
                            </div>
                            <button
                              style={styles.iconBtnLite}
                              onClick={() => addFieldAt(f, selectedFields.length)}
                              title="Add"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            {/* Selected */}
            <Droppable droppableId="selected">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} style={styles.bucket}>
                  <div style={styles.bucketHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <strong>Selected Fields</strong>
                      <span style={styles.countPill}>{selectedFields.length}</span>
                    </div>

                    {viewType === "form" && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button onClick={() => addSplitter("start-split")} style={styles.pillBtn} title="Start a split section">
                          <Columns3 size={12} /> Start Split
                        </button>
                        <button onClick={() => addSplitter("split")} style={styles.pillBtn} title="Split point">
                          <ArrowLeftRight size={12} /> Split
                        </button>
                        <button onClick={() => addSplitter("end-split")} style={styles.pillBtn} title="End split section">
                          <CheckCircle2 size={12} /> End Split
                        </button>
                      </div>
                    )}
                  </div>

                  {selectedFields.length === 0 && (
                    <div style={styles.empty}>
                      <Inbox size={20} color="var(--muted, #5c6d6f)" style={{ marginBottom: 6 }} />
                      <div>Drag fields here to build your view.</div>
                    </div>
                  )}

                  {selectedFields.map((f, i) => {
                    const src = allFields.find((fld) => fld.name === f.sip_field_name);
                    const isSplitter = f.sip_position === "splitter";
                    const label = isSplitter ? f.sip_field_name : src?.label || f.sip_field_name;
                    const FieldIcon = isSplitter ? Wrench : fieldTypeIcon(src);
                    return (
                      <Draggable
                        draggableId={`selected-${f.sip_field_name}`}
                        index={i}
                        key={f.sip_field_name}
                      >
                        {(p) => (
                          <div
                            ref={p.innerRef}
                            {...p.draggableProps}
                            {...p.dragHandleProps}
                            style={{
                              ...styles.fieldItem,
                              ...p.draggableProps.style,
                              background: isSplitter ? "var(--warn-wash, #fef3e2)" : "#ffffff",
                              borderColor: isSplitter ? "#fbbf24" : "var(--line, #dfe7e5)",
                            }}
                          >
                            <GripVertical size={14} color={isSplitter ? "#d97706" : "var(--line, #dfe7e5)"} style={{ flexShrink: 0 }} />
                            <span
                              style={{
                                ...styles.fieldTypeIconWrap,
                                ...(isSplitter ? { background: "#fde68a", color: "#92400e" } : {}),
                              }}
                            >
                              <FieldIcon size={13} />
                            </span>
                            <div style={{ display: "grid", flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600 }}>{label}</div>
                              {f.sip_position !== "splitter" && (
                                <div style={styles.metaSmall}>
                                  {src?.field_type || "—"} · {f.sip_position}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => removeField(f.sip_field_name)}
                              style={styles.iconBtnDanger}
                              title="Remove"
                            >
                              <X size={13} />
                            </button>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.btnGhost}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              ...styles.btnPrimary,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              opacity: saving ? 0.75 : 1,
              cursor: saving ? "not-allowed" : "pointer",
            }}
            disabled={saving}
          >
            {saving ? <Loader2 size={14} style={{ animation: "lv-spin 0.8s linear infinite" }} /> : <Save size={14} />}
            {saving ? "Saving…" : "Save View"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(8, 20, 19, 0.6)",
    backdropFilter: "blur(2px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    backgroundColor: "#ffffff",
    width: "92vw",
    maxWidth: 1100,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    borderRadius: "var(--radius-xl, 20px)",
    boxShadow: "var(--shadow-xl, 0 24px 60px rgba(8,95,84,.18))",
    overflow: "hidden",
  },
  header: {
    padding: "14px 18px",
    borderBottom: "1px solid var(--line, #dfe7e5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(90deg, #085f54, #0d7d6f)",
    color: "#e5e7eb",
  },
  headerIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 8,
    background: "rgba(255,255,255,0.14)",
    color: "#fff",
  },
  crumb: { fontWeight: 600, opacity: 0.85, color: "#e6f4f1" },
  crumbAccent: { fontWeight: 800, color: "#ffffff" },
  sep: { opacity: 0.5 },
  row: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "1fr 1fr 240px",
    padding: 16,
    borderBottom: "1px solid var(--line, #dfe7e5)",
  },
  label: { fontSize: 11, fontWeight: 700, color: "var(--muted, #5c6d6f)", textTransform: "uppercase", letterSpacing: "0.04em" },
  input: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--line, #dfe7e5)",
    outline: "none",
  },
  select: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    outline: "none",
    minWidth: 240,
  },
  segmented: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    background: "var(--card-2, #f1f5f4)",
    borderRadius: 12,
    border: "1px solid var(--line, #dfe7e5)",
    overflow: "hidden",
  },
  segment: {
    padding: "8px 10px",
    textAlign: "center",
    fontWeight: 700,
    cursor: "pointer",
    background: "transparent",
    border: "none",
    transition: "background .12s ease, color .12s ease",
  },
  segmentActive: {
    background: "var(--brand, #0d7d6f)",
    color: "#fff",
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    marginTop: 6,
    color: "var(--ink-2, #1e2e30)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    padding: 16,
    overflow: "hidden",
  },
  bucket: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--card-2, #f1f5f4)",
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 12,
    minHeight: 320,
    maxHeight: "44vh",
    overflowY: "auto",
  },
  bucketHeader: {
    position: "sticky",
    top: 0,
    background: "var(--card-2, #f1f5f4)",
    zIndex: 1,
    borderBottom: "1px solid var(--line, #dfe7e5)",
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  countPill: {
    fontSize: 11,
    fontWeight: 800,
    padding: "2px 8px",
    background: "var(--brand-wash, #e6f4f1)",
    color: "var(--brand-dark, #085f54)",
    border: "1px solid transparent",
    borderRadius: 999,
  },
  searchIcon: {
    position: "absolute",
    left: 9,
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--muted, #5c6d6f)",
    pointerEvents: "none",
  },
  searchInput: {
    border: "1px solid var(--line, #dfe7e5)",
    borderRadius: 10,
    padding: "8px 10px 8px 28px",
    minWidth: 220,
    outline: "none",
    background: "#fff",
  },
  fieldItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px",
    margin: "8px 10px",
    backgroundColor: "#ffffff",
    borderRadius: 10,
    border: "1px solid var(--line, #dfe7e5)",
    boxShadow: "var(--shadow-xs, 0 1px 2px rgba(9,20,19,.05))",
  },
  fieldTypeIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    minWidth: 24,
    borderRadius: 7,
    background: "var(--brand-wash, #e6f4f1)",
    color: "var(--brand-dark, #085f54)",
    flexShrink: 0,
  },
  metaSmall: { fontSize: 12, color: "var(--muted, #5c6d6f)" },
  empty: {
    padding: 24,
    color: "var(--muted, #5c6d6f)",
    textAlign: "center",
    fontSize: 13,
  },
  footer: {
    marginTop: "auto",
    padding: 12,
    borderTop: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  btnPrimary: {
    border: "1px solid var(--brand, #0d7d6f)",
    background: "var(--brand, #0d7d6f)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer",
  },
  btnGhost: {
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--ink-2, #1e2e30)",
    padding: "10px 14px",
    borderRadius: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnDangerOutline: {
    border: "1px solid var(--danger, #dc2626)",
    background: "#fff",
    color: "var(--danger, #dc2626)",
    padding: "8px 12px",
    borderRadius: 10,
    fontWeight: 800,
    cursor: "pointer",
  },
  pillBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    border: "1px solid var(--line, #dfe7e5)",
    background: "#fff",
    color: "var(--ink-2, #1e2e30)",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    transition: "background .12s ease, border-color .12s ease",
  },
  iconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "1px solid rgba(229,231,235,.4)",
    color: "#e5e7eb",
    borderRadius: 10,
    padding: "6px 10px",
    cursor: "pointer",
  },
  iconBtnLite: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--card-2, #f1f5f4)",
    border: "1px solid var(--line, #dfe7e5)",
    color: "var(--brand-dark, #085f54)",
    borderRadius: 8,
    padding: "6px 8px",
    cursor: "pointer",
    fontWeight: 700,
  },
  iconBtnDanger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bad-wash, #fdecec)",
    border: "1px solid transparent",
    color: "var(--danger, #dc2626)",
    borderRadius: 8,
    padding: "6px 8px",
    cursor: "pointer",
    fontWeight: 700,
  },
};

export default UiBuilderModal;